const browserExtensionUrls = [
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-(web-)?extension:\/\//,
];

// The value of a signed link's signature (_hash, and the _expiration it covers), in a URL or a bare
// query string, and in a URL nested in another one (a redirect target), where it is percent-encoded.
const signatureValues = [
    /((?:^|[?&;])_(?:hash|expiration)=)[^&#\s"'<>\\]*/g,
    /((?:%3F|%26)_(?:hash|expiration)%3D)(?:(?!%26)[^&#\s"'<>\\])*/gi,
];

function isPlainObject(value) {
    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}

/**
 * A copy of an event, span or breadcrumb in which no string carries a signed link's signature:
 * whoever holds a signed link opens the litigant's booking, payment or messages (DDT-4466), and
 * the page address travels in the request, the breadcrumbs and the spans' data.
 * Only arrays and plain objects are copied: the SDK's own objects (scopes) stay as they are.
 */
export function withoutSignedLinkSignatures(value) {
    if (typeof value === 'string') {
        return signatureValues.reduce((text, pattern) => text.replace(pattern, '$1[Filtered]'), value);
    }

    if (Array.isArray(value)) {
        return value.map(withoutSignedLinkSignatures);
    }

    if (value !== null && typeof value === 'object' && isPlainObject(value)) {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, withoutSignedLinkSignatures(item)]));
    }

    return value;
}

/**
 * Options for Sentry.init() from the settings the server renders into
 * <meta name="sentry-browser-config"> (App\Twig\SentryExtension).
 *
 * No personal data leaves the browser, and GlitchTip ignores sessions and
 * client reports, so sending them would only cost a request per page view.
 */
export function sentryOptions(config, tracingIntegration) {
    return {
        dsn: config.dsn,
        environment: config.environment ?? undefined,
        release: config.release ?? undefined,
        tracesSampleRate: config.tracesSampleRate,
        initialScope: { tags: config.tags },
        sendDefaultPii: false,
        sendClientReports: false,
        denyUrls: browserExtensionUrls,
        // Errors carry their breadcrumbs; standalone spans (interactions) skip beforeSendTransaction.
        beforeSend: withoutSignedLinkSignatures,
        beforeSendTransaction: withoutSignedLinkSignatures,
        beforeSendSpan: withoutSignedLinkSignatures,
        integrations: (defaults) => defaults
            .filter((integration) => integration.name !== 'BrowserSession')
            .concat(tracingIntegration),
    };
}
